<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import type {
  CareOrderListItem,
  CareOrderListItemChecklist,
  CareOrderChecklistDisplayRow,
} from '@/api/orders'
import { getChecklistList, parseChecklistResponse } from '@/api/checklist'
import { getChecklistDetail, type ChecklistDetailData } from '@/api/itemdetail'
import {
  resolveCoveredRepairFollowUpOutcome,
  bookedTimeFromCareOrderRow,
  bookedTimeFromFollowUpRow,
  bookedTimeFromServiceFields,
} from '@/api/orders'
import type { OrderCardDTO } from '@/api/orders'
import {
  postCareOrderInspectionDetail,
  careOrderInspectionDisplayMediaFromDetail,
  careOrderInspectionFollowUpDisplayMediaFromDetail,
  careOrderInspectionReportIdFromHistory,
  parseCareOrderDetailItemInspection,
  mergeCareOrderInspectionDetailPayloads,
  type CareOrderInspectionDetailPayload,
  type CareOrderInspectionDisplayMediaItem,
  type CareOrderInspectionHistoryEntry,
  type CareOrderInspectionTaskType,
} from '@/api/careOrderInspectionDetail'
import { formatOrderScheduleLineDisplay } from '@/utils/orderScheduleDisplay'
import { formatSeasonDisplayLabel } from '@/utils/seasons'
import imgAlert from '@/assets/alert@2x.png'
import imgNormal from '@/assets/normal@2x.png'
import imgUnable from '@/assets/error@2x.png'
import imgErrorRed from '@/assets/error.red@2x.png'
import imgPending from '@/assets/pending@2x.png'
import imgChevron from '@/assets/downarrow@2x.png'
import imgRightNarrow from '@/assets/right arrow@2x.png'
import imgNavChevron from '@/assets/矩形@2x.png'

interface ItemInspectionRecord {
  key: string
  dateLabel: string
  ymd: string
  serviceDateTimeDisplay: string
  inspectorName: string
  kind: 'initial' | 'followup'
  /** 后台 history.task_type；无则不猜，Report ID 侧为空 */
  taskType?: CareOrderInspectionTaskType
  orderPrimaryId?: number
  resultId?: number
  reportId?: string
  detail?: CareOrderInspectionDetailPayload
}

const props = defineProps<{
  row: CareOrderChecklistDisplayRow
  orderDetail: CareOrderListItem | null
  orderCard: OrderCardDTO | null
}>()

const emit = defineEmits<{
  (e: 'back'): void
}>()

const showAllPhotos = ref(false)
const selectedPhotoIndex = ref<number | null>(null)
const coidRootRef = ref<HTMLElement | null>(null)
const apiInspectionRecords = ref<ItemInspectionRecord[] | null>(null)
/** 仅来自 inspection-detail 的 history，专供按 task_type 取 Report ID */
const apiHistoryForReportIds = ref<
  Array<Pick<CareOrderInspectionHistoryEntry, 'taskType' | 'reportId'>>
>([])
const activeInspectionDetail = ref<CareOrderInspectionDetailPayload | null>(null)
const checklistTemplateDetail = ref<ChecklistDetailData | null>(null)
/** 进入图片预览前，详情页的滚动位置 */
const detailScrollTop = ref<number | null>(null)
/** 在「全部图片」网格内进入单张预览前的滚动位置 */
const photoGridScrollTop = ref<number | null>(null)

function getScrollContainer(): HTMLElement | null {
  const root = coidRootRef.value
  if (!root) return null
  const main = root.closest('.cod-panel')?.querySelector('.cod-main')
  if (main instanceof HTMLElement) return main
  const shell = root.closest('.cod-overlay-shell')
  if (shell instanceof HTMLElement) return shell
  const pageShell = root.closest('.cod-page-shell')
  return pageShell instanceof HTMLElement ? pageShell : null
}

function scrollParentToTop(): void {
  const el = getScrollContainer()
  if (!el) return
  const apply = () => {
    el.scrollTop = 0
  }
  apply()
  requestAnimationFrame(apply)
}

function readScrollTop(): number | null {
  const el = getScrollContainer()
  return el ? el.scrollTop : null
}

async function restoreScrollTop(top: number | null): Promise<void> {
  if (top == null) return
  const saved = top
  const apply = () => {
    const el = getScrollContainer()
    if (el) el.scrollTop = saved
  }
  await nextTick()
  await nextTick()
  apply()
  requestAnimationFrame(() => {
    apply()
    requestAnimationFrame(apply)
  })
}

function clearSavedScrollPositions(): void {
  detailScrollTop.value = null
  photoGridScrollTop.value = null
}

function findMatchingOrderChecklistItem(): CareOrderListItemChecklist | undefined {
  const row = props.row
  return props.orderDetail?.items?.find((item) => {
    if (row.orderItemId != null && typeof item.id === 'number' && item.id === row.orderItemId) {
      return true
    }
    const rawId = item.item_id?.trim()
    return row.itemId != null && rawId === row.itemId
  })
}

function isEmptyDisplayValue(v: string | undefined | null): boolean {
  const s = (v ?? '').trim()
  return !s || s === '—'
}

function mergeInspectionDetailPayloads(
  ...sources: Array<CareOrderInspectionDetailPayload | null | undefined>
): CareOrderInspectionDetailPayload {
  const out: CareOrderInspectionDetailPayload = {}
  for (const src of sources) {
    if (!src) continue
    for (const key of Object.keys(src) as Array<keyof CareOrderInspectionDetailPayload>) {
      const v = src[key]
      const existing = out[key]
      if (Array.isArray(v)) {
        if (!v.length) continue
        if (key === 'photo_urls' && Array.isArray(existing) && existing.length) {
          const seen = new Set(existing)
          out.photo_urls = [...existing, ...v.filter((url) => !seen.has(url))]
        } else {
          out[key] = [...v] as never
        }
      } else if (typeof v === 'string') {
        if (!isEmptyDisplayValue(v)) out[key] = v as never
      } else if (v != null && v !== '') {
        out[key] = v as never
      }
    }
  }
  return out
}

function buildInspectionDetailFromOrderItem(): CareOrderInspectionDetailPayload | null {
  return parseCareOrderDetailItemInspection(findMatchingOrderChecklistItem())
}

function orderItemSnapshot(): Record<string, unknown> | null {
  const item = findMatchingOrderChecklistItem()
  if (!item) return null
  const raw = item as Record<string, unknown>
  const snap = raw.snapshot
  if (snap && typeof snap === 'object' && !Array.isArray(snap)) {
    const obj = snap as Record<string, unknown>
    if (Object.keys(obj).length) return obj
  }
  const insp = raw.inspection_details
  if (insp && typeof insp === 'object' && !Array.isArray(insp)) {
    return insp as Record<string, unknown>
  }
  return null
}

function seasonsFromSnapshot(raw: unknown): string[] {
  if (!raw) return []
  if (Array.isArray(raw)) {
    return raw
      .map((s) => {
        if (typeof s === 'string') return formatSeasonLabel(s)
        if (s && typeof s === 'object') {
          const o = s as Record<string, unknown>
          return formatSeasonLabel(String(o.label ?? o.value ?? ''))
        }
        return ''
      })
      .filter(Boolean)
  }
  if (typeof raw === 'string') {
    return raw
      .split(/[,;/]/)
      .map((s) => formatSeasonLabel(s.trim()))
      .filter(Boolean)
  }
  return []
}

function buildInspectionDetailFromOrderContext(): CareOrderInspectionDetailPayload {
  const payload: CareOrderInspectionDetailPayload = {}
  const assign =
    props.orderDetail?.assignment_text?.trim() ||
    props.orderCard?.services?.[0]?.assignment?.trim()
  if (assign) {
    payload.assignment_text = assign
    payload.inspector_name = assign
  }
  const serviceDate =
    props.orderDetail?.service_date?.trim() || props.orderCard?.serviceDate?.trim()
  if (serviceDate) payload.service_date = serviceDate
  const serviceTime = props.orderDetail?.service_time_text?.trim()
  if (serviceTime) payload.service_time_text = serviceTime
  return payload
}

function resolveChecklistTemplateId(item: CareOrderListItemChecklist): number | undefined {
  const r = item as Record<string, unknown>
  for (const key of [
    'checklist_id',
    'care_checklist_id',
    'library_checklist_id',
    'checklist_template_id',
    'template_checklist_id',
  ]) {
    const n = Number(r[key])
    if (Number.isFinite(n) && n > 0) return Math.trunc(n)
  }
  return undefined
}

function formatSeasonLabel(raw: string): string {
  return formatSeasonDisplayLabel(raw)
}

function splitDescriptionParagraphs(text: string): string[] {
  const t = text.trim()
  if (!t) return []
  const byParagraph = t
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
  if (byParagraph.length > 1) return byParagraph
  return t
    .split(/\n/)
    .map((p) => p.trim())
    .filter(Boolean)
}

async function loadChecklistTemplateDetail(): Promise<void> {
  const item = findMatchingOrderChecklistItem()
  if (!item) {
    checklistTemplateDetail.value = null
    return
  }

  let templateId = resolveChecklistTemplateId(item)
  const businessId = item.item_id?.trim()

  if (!templateId && businessId) {
    try {
      const raw = await getChecklistList({
        page: 1,
        page_size: 1,
        search_type: 'id',
        keyword: businessId,
      })
      const parsed = parseChecklistResponse(raw)
      const hit = parsed?.list?.[0]
      if (hit?.id) templateId = hit.id
    } catch {
      /* ignore */
    }
  }

  if (!templateId) {
    checklistTemplateDetail.value = null
    return
  }

  try {
    const response = await getChecklistDetail({ id: templateId })
    if (response.code === 200 && response.data) {
      checklistTemplateDetail.value = response.data
      return
    }
  } catch {
    /* ignore */
  }
  checklistTemplateDetail.value = null
}

function displayMediaFromInspectionDetail(
  detail: CareOrderInspectionDetailPayload | null | undefined,
): CareOrderInspectionDisplayMediaItem[] {
  return careOrderInspectionDisplayMediaFromDetail(detail)
}

const allPhotoUrls = computed(() => {
  const fromEffective = displayMediaFromInspectionDetail(effectiveInspectionDetail.value)
  if (fromEffective.length) return fromEffective
  return displayMediaFromInspectionDetail(activeInspectionRecord.value?.detail)
})

const followUpPhotoUrls = computed(() =>
  careOrderInspectionFollowUpDisplayMediaFromDetail(effectiveInspectionDetail.value),
)

/** 大图预览数据源：初检 vs 跟进报告照片，互不混用 */
const photoViewerSource = ref<'inspection' | 'followup'>('inspection')

const previewPhotoUrls = computed(() => allPhotoUrls.value.slice(0, 3))
const hasMorePhotos = computed(() => allPhotoUrls.value.length > 3)

const isNeedsAttention = computed(() => props.row.statusTone === 'attention')
const isNormalDetail = computed(() => props.row.statusTone === 'normal')
const isUnableDetail = computed(() => props.row.statusTone === 'unable')
const isPendingDetail = computed(() => props.row.statusTone === 'pending')
/** Covered Repair 初检或已用 FU outcome 覆盖为 Normal/Unable 的行 */
const isCoveredRepairCase = computed(
  () =>
    props.row.statusTone === 'major-covered' || props.row.coveredRepairOutcome != null,
)
const isMajorCoveredDetail = computed(() => isCoveredRepairCase.value)
const isMajorDetail = computed(() => props.row.statusTone === 'major')
const majorCoveredFollowUpOutcome = computed((): 'normal' | 'unable' | null => {
  if (!isCoveredRepairCase.value) return null
  if (props.row.coveredRepairOutcome) return props.row.coveredRepairOutcome
  return resolveCoveredRepairFollowUpOutcome(props.row, props.orderDetail, props.orderCard)
})
const isMajorCoveredResolvedNormal = computed(
  () => majorCoveredFollowUpOutcome.value === 'normal',
)
const isMajorCoveredResolvedUnable = computed(
  () => majorCoveredFollowUpOutcome.value === 'unable',
)
/** Covered Repair：进行中只显示 Major Issue；明确修好/无法修才画虚线+第二步 */
const showMajorStatusTimeline = computed(() => isCoveredRepairCase.value)
const showMajorCoveredFollowUpStep = computed(
  () =>
    majorCoveredFollowUpOutcome.value === 'normal' ||
    majorCoveredFollowUpOutcome.value === 'unable',
)
/** Major Issue（非 Covered Repair）初检详情：单块报告布局 */
const isMajorIssueReportPage = computed(() => isMajorDetail.value)
/** 普通 Normal/Unable 用紧凑布局；Covered Repair 过渡态仍走完整报告+跟进块 */
const isCompactDetail = computed(
  () =>
    (isNormalDetail.value || isUnableDetail.value) && !isCoveredRepairCase.value,
)
const showInspectionDetailBlock = computed(
  () => isNeedsAttention.value || isCoveredRepairCase.value,
)

watch(
  () => props.row.orderItemId,
  () => {
    showAllPhotos.value = false
    selectedPhotoIndex.value = null
    clearSavedScrollPositions()
    selectedInspectionRecordKey.value = null
    reportDateDropOpen.value = false
    void nextTick(() => scrollParentToTop())
  },
)

const statusIconSrc = computed(() => {
  if (isNormalDetail.value) return imgNormal
  if (isUnableDetail.value) return imgUnable
  if (isPendingDetail.value) return imgPending
  if (isNeedsAttention.value) return imgAlert
  if (isMajorDetail.value || isMajorCoveredDetail.value) return imgErrorRed
  return imgAlert
})

const statusTextClass = computed(() => ({
  'coid-status-txt': true,
  'coid-status-txt--attention': isNeedsAttention.value,
  'coid-status-txt--normal': isNormalDetail.value,
  'coid-status-txt--unable': isUnableDetail.value,
  'coid-status-txt--pending': isPendingDetail.value,
  'coid-status-txt--major': isMajorDetail.value,
}))

const statusDisplayLabel = computed(() => props.row.statusLabel || '—')

const conditionRateDisplay = computed(() => {
  const fromApi = effectiveInspectionDetail.value?.condition_rate?.trim()
  return fromApi || '—'
})

const displayLocation = computed(() => {
  const snap = orderItemSnapshot()
  const fromSnap = snap ? String(snap.section ?? '').trim() : ''
  if (fromSnap) return fromSnap
  const fromTemplate = checklistTemplateDetail.value?.section?.trim()
  if (fromTemplate) return fromTemplate
  const loc = (props.row.location ?? '').trim()
  return loc || '—'
})

const displaySection = computed(() => {
  const snap = orderItemSnapshot()
  const fromSnap = snap ? String(snap.section ?? '').trim() : ''
  if (fromSnap) return fromSnap
  const fromTemplate = checklistTemplateDetail.value?.section?.trim()
  if (fromTemplate) return fromTemplate
  const item = findMatchingOrderChecklistItem()
  const fromItem = item
    ? String((item as Record<string, unknown>).section ?? '').trim()
    : ''
  if (fromItem) return fromItem
  return '—'
})

function openPhoto(index: number) {
  photoViewerSource.value = 'inspection'
  if (showAllPhotos.value) {
    photoGridScrollTop.value = readScrollTop()
  } else {
    detailScrollTop.value = readScrollTop()
  }
  selectedPhotoIndex.value = index
}

function openFollowUpPhoto(index: number) {
  photoViewerSource.value = 'followup'
  detailScrollTop.value = readScrollTop()
  selectedPhotoIndex.value = index
}

function openAllPhotos() {
  detailScrollTop.value = readScrollTop()
  selectedPhotoIndex.value = null
  showAllPhotos.value = true
}

async function onBackClick() {
  if (selectedPhotoIndex.value !== null) {
    const gridTop = photoGridScrollTop.value
    const detailTop = detailScrollTop.value
    selectedPhotoIndex.value = null
    if (showAllPhotos.value) {
      await restoreScrollTop(gridTop)
      photoGridScrollTop.value = null
    } else {
      await restoreScrollTop(detailTop)
      detailScrollTop.value = null
    }
    return
  }
  if (showAllPhotos.value) {
    const detailTop = detailScrollTop.value
    showAllPhotos.value = false
    photoGridScrollTop.value = null
    await restoreScrollTop(detailTop)
    detailScrollTop.value = null
    return
  }
  clearSavedScrollPositions()
  emit('back')
}

const selectedPhotoSrc = computed((): CareOrderInspectionDisplayMediaItem | null => {
  const i = selectedPhotoIndex.value
  if (i == null) return null
  const list =
    photoViewerSource.value === 'followup' ? followUpPhotoUrls.value : allPhotoUrls.value
  return list[i] ?? null
})

const checkTitle = computed(() => {
  const snap = orderItemSnapshot()
  const fromSnap = snap
    ? String(snap.title ?? snap.item_name ?? snap.name ?? '').trim()
    : ''
  if (fromSnap) return fromSnap
  const fromTemplate = checklistTemplateDetail.value?.item_name?.trim()
  if (fromTemplate) return fromTemplate
  const fromRow = props.row.title?.trim()
  if (fromRow) return fromRow
  const item = findMatchingOrderChecklistItem()
  return item?.item_name?.trim() || item?.item_id?.trim() || '—'
})

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function formatServiceDateDot(rawDate: string | undefined): string {
  const raw = rawDate?.trim()
  if (!raw) return '—'

  const parsed = Date.parse(raw.replace(/\./g, '/'))
  if (!Number.isNaN(parsed)) {
    const dt = new Date(parsed)
    return `${MONTH_ABBR[dt.getMonth()]}.${dt.getDate()}, ${dt.getFullYear()}`
  }

  const m = raw.match(/([A-Za-z]{3})\w*[\s.,/-]+(\d{1,2})(?:[\s.,/-]+(\d{4}))?/i)
  if (m?.[1] && m[2]) {
    const year = m[3] ?? String(new Date().getFullYear())
    return `${m[1].slice(0, 3)}.${m[2]}, ${year}`
  }

  const formatted = formatOrderScheduleLineDisplay(raw)
  if (formatted !== '—') {
    const head = formatted.match(/^([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{4})/)
    if (head?.[1] && head[2] && head[3]) {
      return `${head[1]}.${head[2]}, ${head[3]}`
    }
  }

  return raw
}

function formatServiceTimeAmPm(rawTime: string | undefined): string {
  const raw = rawTime?.trim()
  if (!raw) return ''

  const m12 = raw.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i)
  if (m12?.[1] != null && m12[2] != null && m12[3] != null) {
    return `${String(Number(m12[1])).padStart(2, '0')}:${m12[2]} ${m12[3].toUpperCase()}`
  }

  const m24 = raw.match(/(\d{1,2}):(\d{2})/)
  if (m24) {
    let h = Number(m24[1])
    const min = m24[2]
    const ap = h >= 12 ? 'PM' : 'AM'
    if (h === 0) h = 12
    else if (h > 12) h -= 12
    return `${String(h).padStart(2, '0')}:${min} ${ap}`
  }

  return raw
}

function formatServiceDateAndTime(dateRaw?: string, timeRaw?: string): string {
  const booked = bookedTimeFromServiceFields(timeRaw, undefined, undefined, dateRaw)
  if (booked) return booked

  const combined = timeRaw?.trim()
  if (combined && /,\s*\d{1,2}:\d{2}\s*(AM|PM)/i.test(combined)) {
    const formatted = formatOrderScheduleLineDisplay(combined)
    if (formatted !== '—') return formatted
    return combined
      .replace(/([A-Za-z]{3})\.\s+(\d)/, '$1.$2')
      .replace(/([A-Za-z]{3})\s+(\d)/, '$1.$2')
  }

  const datePart = formatServiceDateDot(dateRaw)
  const timePart = formatServiceTimeAmPm(timeRaw)
  if (datePart === '—' && !timePart) return '—'
  if (!timePart) return datePart
  if (datePart === '—') return timePart
  return `${datePart}, ${timePart}`
}

const reportDateLabel = computed(() => {
  const raw = props.orderDetail?.service_date?.trim() || props.orderCard?.serviceDate?.trim()
  if (!raw) return '—'
  return formatServiceDateDot(raw)
})

const selectedInspectionRecordKey = ref<string | null>(null)
const reportDateDropOpen = ref(false)
const dateBtnRef = ref<HTMLElement | null>(null)
const dateDropRef = ref<HTMLElement | null>(null)

function parseDateRawToYmd(raw: string | undefined): string | null {
  const text = raw?.trim()
  if (!text) return null
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso?.[1] && iso[2] && iso[3]) return `${iso[1]}-${iso[2]}-${iso[3]}`
  const parsed = Date.parse(text.replace(/\./g, '/'))
  if (!Number.isNaN(parsed)) {
    const dt = new Date(parsed)
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
  }
  const m = text.match(/([A-Za-z]{3})\w*[\s.,/-]+(\d{1,2})(?:[\s.,/-]+(\d{4}))?/i)
  if (m?.[1] && m[2]) {
    const year = m[3] ?? String(new Date().getFullYear())
    const monStr = m[1].slice(0, 3).toLowerCase()
    const monthIdx = MONTH_ABBR.findIndex((x) => x.toLowerCase() === monStr)
    const mo = monthIdx >= 0 ? monthIdx + 1 : 12
    return `${year}-${String(mo).padStart(2, '0')}-${String(Number(m[2])).padStart(2, '0')}`
  }
  return null
}

function readRecordString(row: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = row[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

function technicianNameFromFollowUp(fu: {
  assignment_text?: string
  technician?: Record<string, unknown> | null
}): string {
  const tech = fu.technician
  if (tech && typeof tech === 'object') {
    const name = tech.name ?? tech.label ?? tech.display_name ?? tech.displayName
    if (name != null && String(name).trim()) return String(name).trim()
  }
  return fu.assignment_text?.trim() || 'Tom Pearson'
}

function followUpMatchesChecklistItem(
  fu: { source_order_item_id?: number | null },
  orderItemId?: number,
): boolean {
  if (orderItemId == null) return true
  return fu.source_order_item_id != null && fu.source_order_item_id === orderItemId
}

function resolveOrderPrimaryIdForInspection(): number | undefined {
  const fromDetail = props.orderDetail?.id
  if (typeof fromDetail === 'number' && fromDetail > 0) return Math.trunc(fromDetail)
  const fromCard = props.orderCard?.orderPrimaryId
  if (typeof fromCard === 'number' && fromCard > 0) return Math.trunc(fromCard)
  return undefined
}

function mapApiHistoryEntryToRecord(entry: CareOrderInspectionHistoryEntry): ItemInspectionRecord {
  const ymd = parseDateRawToYmd(entry.serviceDate) ?? entry.key
  const orderAssign =
    props.orderDetail?.assignment_text?.trim() ||
    props.orderCard?.services?.[0]?.assignment?.trim() ||
    ''
  const dateRaw = entry.serviceDate?.trim() ?? ''
  const dateLabel =
    dateRaw && (dateRaw.includes('.') || dateRaw.includes(','))
      ? dateRaw
      : formatServiceDateDot(dateRaw)
  const serviceDateTimeDisplay =
    entry.serviceTimeText?.trim() ||
    formatServiceDateAndTime(entry.serviceDate, entry.serviceTimeText)
  return {
    key: entry.key,
    dateLabel,
    ymd,
    serviceDateTimeDisplay,
    inspectorName: entry.assignmentText?.trim() || orderAssign,
    kind: entry.recordKind,
    taskType: entry.taskType,
    orderPrimaryId: entry.orderPrimaryId,
    resultId: entry.inspectionId,
    reportId: entry.reportId,
    detail: entry.detail,
  }
}

function buildFallbackInspectionHistoryRecords(): ItemInspectionRecord[] {
  const order = props.orderDetail
  const row = props.row
  const records: ItemInspectionRecord[] = []
  const seen = new Set<string>()

  const pushRecord = (entry: ItemInspectionRecord) => {
    if (seen.has(entry.key)) return
    seen.add(entry.key)
    records.push(entry)
  }

  const initialDateRaw =
    order?.service_date?.trim() || props.orderCard?.serviceDate?.trim() || undefined
  const initialYmd = parseDateRawToYmd(initialDateRaw) ?? 'current-initial'
  const initialBooked = order ? bookedTimeFromCareOrderRow(order) : null
  pushRecord({
    key: `initial-${initialYmd}`,
    dateLabel: initialDateRaw ? formatServiceDateDot(initialDateRaw) : reportDateLabel.value,
    ymd: initialYmd,
    serviceDateTimeDisplay:
      initialBooked ??
      formatServiceDateAndTime(initialDateRaw, order?.service_time_text?.trim()),
    inspectorName:
      order?.assignment_text?.trim() ||
      props.orderCard?.services?.[0]?.assignment?.trim() ||
      'John Larry',
    kind: 'initial',
    detail: buildInspectionDetailFromOrderItem() ?? undefined,
  })

  for (const fu of order?.followups ?? []) {
    if (!followUpMatchesChecklistItem(fu, row.orderItemId)) continue
    const fuDateRaw = fu.follow_up_date?.trim()
    if (!fuDateRaw) continue
    const fuYmd = parseDateRawToYmd(fuDateRaw) ?? `fu-${fu.id ?? records.length}`
    const fuBooked = bookedTimeFromFollowUpRow(fu)
    const fuTime =
      fu.follow_up_time_text?.trim() ||
      [fu.follow_up_time_start, fu.follow_up_time_end].filter(Boolean).join(' - ')
    pushRecord({
      key: `followup-${fuYmd}-${fu.id ?? records.length}`,
      dateLabel: formatServiceDateDot(fuDateRaw),
      ymd: fuYmd,
      serviceDateTimeDisplay: fuBooked ?? formatServiceDateAndTime(fuDateRaw, fuTime),
      inspectorName: technicianNameFromFollowUp(fu),
      kind: 'followup',
    })
  }

  const checklistItem = order?.items?.find((item) => {
    if (row.orderItemId != null && typeof item.id === 'number' && item.id === row.orderItemId) {
      return true
    }
    const rawId = item.item_id?.trim()
    return row.itemId != null && rawId === row.itemId
  })
  if (checklistItem) {
    const raw = checklistItem as Record<string, unknown>
    for (const key of ['inspection_history', 'inspection_records', 'history', 'records']) {
      const list = raw[key]
      if (!Array.isArray(list)) continue
      for (let i = 0; i < list.length; i++) {
        const entry = list[i]
        if (!entry || typeof entry !== 'object') continue
        const rowObj = entry as Record<string, unknown>
        const dateRaw = readRecordString(
          rowObj,
          'service_date',
          'inspection_date',
          'date',
          'serviceDate',
          'report_date',
        )
        if (!dateRaw) continue
        const ymd = parseDateRawToYmd(dateRaw) ?? `hist-${i}`
        const timeRaw = readRecordString(
          rowObj,
          'service_time_text',
          'time_text',
          'serviceTimeText',
          'inspection_time',
        )
        const taskType =
          (() => {
            const v = String(rowObj.task_type ?? rowObj.taskType ?? '')
              .trim()
              .toLowerCase()
            if (v === 'followup' || v === 'follow_up' || v === 'follow-up') return 'followup' as const
            if (v === 'order') return 'order' as const
            return undefined
          })()
        pushRecord({
          key: `history-${ymd}-${i}`,
          dateLabel: formatServiceDateDot(dateRaw),
          ymd,
          serviceDateTimeDisplay: formatServiceDateAndTime(dateRaw, timeRaw),
          inspectorName:
            readRecordString(rowObj, 'assignment_text', 'inspector', 'technician_name') ||
            order?.assignment_text?.trim() ||
            'John Larry',
          kind:
            taskType === 'followup'
              ? 'followup'
              : taskType === 'order'
                ? 'initial'
                : rowObj.kind === 'followup' || rowObj.record_type === 'followup'
                  ? 'followup'
                  : 'initial',
          taskType,
          reportId: readRecordString(rowObj, 'report_id', 'reportId') || undefined,
        })
      }
    }
  }

  return records.sort((a, b) => {
    if (a.ymd === b.ymd) return a.kind === 'followup' ? -1 : 1
    return a.ymd < b.ymd ? 1 : -1
  })
}

const inspectionHistoryRecords = computed(() => {
  // 日期按钮只信 inspection-detail history / selected_history，不用订单 service_date 本地拼
  return apiInspectionRecords.value?.length ? apiInspectionRecords.value : []
})

async function loadInspectionDetail(
  orderPrimaryId?: number,
  resultId?: number,
): Promise<void> {
  const orderItemId = props.row.orderItemId
  if (orderItemId == null || orderItemId <= 0) {
    apiInspectionRecords.value = null
    apiHistoryForReportIds.value = []
    activeInspectionDetail.value = null
    return
  }

  try {
    const body: { order_item_id: number; id?: number; result_id?: number } = {
      order_item_id: Math.trunc(orderItemId),
    }
    const pk = orderPrimaryId ?? resolveOrderPrimaryIdForInspection()
    if (pk != null && pk > 0) body.id = pk
    if (resultId != null && resultId > 0) body.result_id = Math.trunc(resultId)

    const result = await postCareOrderInspectionDetail(body)
    if (result.records.length) {
      apiInspectionRecords.value = result.records.map(mapApiHistoryEntryToRecord)
      apiHistoryForReportIds.value = result.records.map((r) => ({
        taskType: r.taskType,
        reportId: r.reportId,
      }))
    } else {
      apiInspectionRecords.value = null
      // 二次请求未带 history 时保留已有 inspection-detail history，避免 Report ID 被冲掉
    }
    activeInspectionDetail.value = mergeCareOrderInspectionDetailPayloads(
      buildInspectionDetailFromOrderItem(),
      result.detail,
    )
  } catch {
    apiInspectionRecords.value = null
    apiHistoryForReportIds.value = []
    activeInspectionDetail.value = buildInspectionDetailFromOrderItem()
  }
}

const activeInspectionRecord = computed(() => {
  const list = inspectionHistoryRecords.value
  if (!list.length) return null
  const key = selectedInspectionRecordKey.value
  if (key) {
    const hit = list.find((r) => r.key === key)
    if (hit) return hit
  }
  return list[0]!
})

const effectiveInspectionDetail = computed((): CareOrderInspectionDetailPayload | null => {
  const merged = mergeInspectionDetailPayloads(
    buildInspectionDetailFromOrderContext(),
    buildInspectionDetailFromOrderItem(),
    activeInspectionRecord.value?.detail,
    activeInspectionDetail.value,
  )
  const hasData = Object.entries(merged).some(([, v]) => {
    if (Array.isArray(v)) return v.length > 0
    return v != null && v !== ''
  })
  return hasData ? merged : null
})

watch(
  inspectionHistoryRecords,
  (list) => {
    if (!list.length) {
      selectedInspectionRecordKey.value = null
      return
    }
    if (!list.some((r) => r.key === selectedInspectionRecordKey.value)) {
      selectedInspectionRecordKey.value = list[0]!.key
    }
  },
  { immediate: true },
)

const reportId = computed(() => {
  const fromHistory = careOrderInspectionReportIdFromHistory(
    apiHistoryForReportIds.value,
    'order',
  )
  if (fromHistory) return fromHistory
  // 同响应 item.report_display.report_id（history 为空时仍有）
  return effectiveInspectionDetail.value?.report_id?.trim() || '—'
})

const followUpReportId = computed(() => {
  const fromHistory = careOrderInspectionReportIdFromHistory(
    apiHistoryForReportIds.value,
    'followup',
  )
  if (fromHistory) return fromHistory
  return effectiveInspectionDetail.value?.follow_up_report_id?.trim() || '—'
})

const reportDateDisplay = computed(
  () => activeInspectionRecord.value?.dateLabel?.trim() || '—',
)

const viewingFollowUpInspectionRecord = computed(() => {
  if (effectiveInspectionDetail.value?.record_kind === 'followup') return true
  if (effectiveInspectionDetail.value?.record_kind === 'initial') return false
  return activeInspectionRecord.value?.kind === 'followup'
})

function toggleReportDateDrop(): void {
  if (inspectionHistoryRecords.value.length <= 1) return
  reportDateDropOpen.value = !reportDateDropOpen.value
}

async function onSelectInspectionRecord(record: ItemInspectionRecord): Promise<void> {
  const changed = selectedInspectionRecordKey.value !== record.key
  if (changed) {
    selectedInspectionRecordKey.value = record.key
    showAllPhotos.value = false
    selectedPhotoIndex.value = null
    clearSavedScrollPositions()
    scrollParentToTop()
  }
  reportDateDropOpen.value = false

  if (record.detail) {
    activeInspectionDetail.value = record.detail
    return
  }
  if (props.row.orderItemId != null) {
    await loadInspectionDetail(record.orderPrimaryId, record.resultId)
    if (changed) {
      selectedInspectionRecordKey.value = record.key
    }
  }
}

watch(
  () => [props.row.orderItemId, props.orderDetail?.id, props.orderCard?.orderPrimaryId] as const,
  () => {
    selectedInspectionRecordKey.value = null
    reportDateDropOpen.value = false
    void loadInspectionDetail()
    void loadChecklistTemplateDetail()
  },
  { immediate: true },
)

function onDocPointerDown(ev: MouseEvent): void {
  if (!reportDateDropOpen.value) return
  const t = ev.target as Node
  if (dateBtnRef.value?.contains(t)) return
  if (dateDropRef.value?.contains(t)) return
  reportDateDropOpen.value = false
}

onMounted(() => {
  document.addEventListener('mousedown', onDocPointerDown)
})
onUnmounted(() => document.removeEventListener('mousedown', onDocPointerDown))

const serviceDateTime = computed(() => {
  const d = effectiveInspectionDetail.value
  if (d?.service_date?.trim() || d?.service_time_text?.trim()) {
    const fromApi = bookedTimeFromServiceFields(
      d.service_time_text,
      undefined,
      undefined,
      d.service_date,
    )
    if (fromApi) return fromApi
    return formatServiceDateAndTime(d.service_date, d.service_time_text)
  }
  const fromRecord = activeInspectionRecord.value?.serviceDateTimeDisplay?.trim()
  if (fromRecord && fromRecord !== '—') return fromRecord
  if (props.orderDetail) {
    const booked = bookedTimeFromCareOrderRow(props.orderDetail)
    if (booked) return booked
  }
  return formatServiceDateAndTime(
    props.orderDetail?.service_date?.trim() || props.orderCard?.serviceDate?.trim(),
    props.orderDetail?.service_time_text?.trim(),
  )
})

function readInspectorDisplayName(raw: string | undefined): string {
  const s = raw?.trim()
  if (!s || s === '—') return ''
  return s
}

const inspectorName = computed(() => {
  const d = effectiveInspectionDetail.value
  const fromDetail =
    readInspectorDisplayName(d?.assignment_text) || readInspectorDisplayName(d?.inspector_name)
  if (fromDetail) return fromDetail
  const fromRecord = readInspectorDisplayName(activeInspectionRecord.value?.inspectorName)
  if (fromRecord) return fromRecord
  return (
    props.orderDetail?.assignment_text?.trim() ||
    props.orderCard?.services?.[0]?.assignment?.trim() ||
    '—'
  )
})

const followUpTechnicianName = computed(
  () => effectiveInspectionDetail.value?.follow_up_technician?.trim() || '—',
)

const followUpDateTimeDisplay = computed(() => {
  const d = effectiveInspectionDetail.value
  if (d?.follow_up_date?.trim() || d?.follow_up_time_text?.trim()) {
    const fromApi = bookedTimeFromServiceFields(
      d.follow_up_time_text,
      undefined,
      undefined,
      d.follow_up_date,
    )
    if (fromApi) return fromApi
    return formatServiceDateAndTime(d.follow_up_date, d.follow_up_time_text)
  }
  return '—'
})

const seasons = computed(() => {
  const snap = orderItemSnapshot()
  const fromSnap = seasonsFromSnapshot(snap?.seasons)
  if (fromSnap.length) return fromSnap
  const fromTemplate = checklistTemplateDetail.value?.seasons
  if (!fromTemplate?.length) return []
  return fromTemplate.map((s) => formatSeasonLabel(String(s))).filter(Boolean)
})

const descriptionParagraphs = computed(() => {
  const snap = orderItemSnapshot()
  const fromSnap = snap ? String(snap.description ?? snap.description_zh ?? '').trim() : ''
  if (fromSnap) return splitDescriptionParagraphs(fromSnap)
  const fromTemplate = checklistTemplateDetail.value?.description?.trim()
  if (!fromTemplate) return []
  return splitDescriptionParagraphs(fromTemplate)
})

const locationDetail = computed(() => {
  const fromApi =
    effectiveInspectionDetail.value?.location_detail?.trim() ||
    effectiveInspectionDetail.value?.location?.trim()
  if (fromApi) return fromApi
  return '—'
})

const locationNote = computed(() => effectiveInspectionDetail.value?.location_note?.trim() || '')

const taskNote = computed(() => effectiveInspectionDetail.value?.task_note?.trim() || '')

const timeNote = computed(() => effectiveInspectionDetail.value?.time_note?.trim() || '')

const followUpNotesParagraphs = computed(() => {
  const apiNotes = effectiveInspectionDetail.value?.follow_up_notes
  if (Array.isArray(apiNotes) && apiNotes.length) return apiNotes
  if (typeof apiNotes === 'string' && apiNotes.trim()) return [apiNotes.trim()]
  return []
})

const conditionSummary = computed(() => {
  const fromApi =
    effectiveInspectionDetail.value?.condition_summary?.trim() ||
    effectiveInspectionDetail.value?.condition?.trim()
  return fromApi || '—'
})

const taskPlainLabel = computed(() => {
  const d = effectiveInspectionDetail.value
  return d?.task_label?.trim() || d?.task?.trim() || '—'
})

const timePlainLabel = computed(() => {
  const d = effectiveInspectionDetail.value
  return d?.time_label?.trim() || d?.time?.trim() || '—'
})

const conditionNote = computed(() => effectiveInspectionDetail.value?.condition_note?.trim() || '')

const generalDescriptionParagraphs = computed(() => {
  const apiGeneral = effectiveInspectionDetail.value?.general_description
  if (Array.isArray(apiGeneral) && apiGeneral.length) return apiGeneral
  if (typeof apiGeneral === 'string' && apiGeneral.trim()) return [apiGeneral.trim()]
  return []
})
</script>

<template>
  <div ref="coidRootRef" class="coid">
    <template v-if="selectedPhotoIndex !== null">
      <button type="button" class="coid-back" @click="onBackClick">
        <img :src="imgNavChevron" class="coid-back-ic" alt="" aria-hidden="true" />
        <span class="coid-back-txt">Back</span>
      </button>
      <video
        v-if="selectedPhotoSrc?.isVideo"
        :src="selectedPhotoSrc.url"
        class="coid-photo-preview"
        controls
        playsinline
        preload="metadata"
      />
      <img
        v-else-if="selectedPhotoSrc"
        :src="selectedPhotoSrc.url"
        class="coid-photo-preview"
        alt=""
      />
    </template>

    <template v-else-if="showAllPhotos">
      <button type="button" class="coid-back" @click="onBackClick">
        <img :src="imgNavChevron" class="coid-back-ic" alt="" aria-hidden="true" />
        <span class="coid-back-txt">Back</span>
      </button>
      <div class="coid-photo-grid">
        <button
          v-for="(item, i) in allPhotoUrls"
          :key="`all-${i}`"
          type="button"
          class="coid-photo-grid-btn"
          @click="openPhoto(i)"
        >
          <video
            v-if="item.isVideo"
            :src="item.url"
            class="coid-photo-grid-item"
            muted
            playsinline
            preload="metadata"
          />
          <img v-else :src="item.url" class="coid-photo-grid-item" alt="" />
        </button>
      </div>
    </template>

    <template v-else>
      <button type="button" class="coid-back" @click="onBackClick">
        <img :src="imgNavChevron" class="coid-back-ic" alt="" aria-hidden="true" />
        <span class="coid-back-txt">Back</span>
      </button>
      <div class="coid-block">
        <div class="coid-row">
          <span class="coid-label coid-label--check">Check:</span>
          <span class="coid-value coid-value--title">{{ checkTitle }}</span>
        </div>
        <div class="coid-row">
          <span class="coid-label">{{ isPendingDetail ? 'Section:' : 'Location:' }}</span>
          <span class="coid-value coid-value--location">{{
            isPendingDetail ? displaySection : displayLocation
          }}</span>
        </div>
        <div class="coid-row coid-row--top">
          <span class="coid-label">Seasons:</span>
          <div class="coid-value">
            <div class="coid-seasons">
              <span
                v-for="s in seasons"
                :key="s"
                class="coid-season-pill"
                :class="{
                  'coid-season-pill--summer': s === 'Summer',
                  'coid-season-pill--winter': s === 'Winter',
                }"
                >{{ s }}</span
              >
            </div>
          </div>
        </div>
        <div class="coid-row coid-row--top">
          <span class="coid-label">Description:</span>
          <div class="coid-value coid-value--stack coid-value--description">
            <p
              v-for="(p, i) in descriptionParagraphs"
              :key="i"
              class="coid-para coid-para--description"
            >
              {{ p }}
            </p>
          </div>
        </div>
      </div>

      <div class="coid-divider" />

      <div v-if="isPendingDetail" class="coid-block">
        <div class="coid-row">
          <span class="coid-label">Status:</span>
          <span class="coid-value coid-value--status">
            <img :src="statusIconSrc" class="coid-status-ic" alt="" aria-hidden="true" />
            <span :class="statusTextClass">{{ statusDisplayLabel }}</span>
          </span>
        </div>
      </div>

      <!-- Major Issue 初检详情（设计稿：单行 Status + 连续报告块，无 Follow-up） -->
      <div v-else-if="isMajorIssueReportPage" class="coid-block coid-block--major-report">
        <div class="coid-row">
          <span class="coid-label">Report ID:</span>
          <div class="coid-value coid-value--report">
            <span class="coid-report-id coid-value--body-text">{{ reportId }}</span>
            <div class="coid-date-anchor">
              <button
                ref="dateBtnRef"
                type="button"
                class="coid-date-btn"
                :aria-expanded="reportDateDropOpen"
                aria-haspopup="listbox"
                aria-label="View past inspection records"
                @click.stop="toggleReportDateDrop"
              >
                <span class="coid-date-txt">{{ reportDateDisplay }}</span>
                <img :src="imgChevron" class="coid-date-chev" alt="" aria-hidden="true" />
              </button>
              <div
                v-if="reportDateDropOpen && inspectionHistoryRecords.length > 1"
                ref="dateDropRef"
                class="coid-date-drop"
                role="listbox"
                aria-label="Past inspection records"
                @mousedown.stop
                @click.stop
              >
                <button
                  v-for="rec in inspectionHistoryRecords"
                  :key="rec.key"
                  type="button"
                  class="coid-date-drop-item"
                  :class="{ 'coid-date-drop-item--active': rec.key === activeInspectionRecord?.key }"
                  role="option"
                  :aria-selected="rec.key === activeInspectionRecord?.key"
                  @click="onSelectInspectionRecord(rec)"
                >
                  {{ rec.dateLabel }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="coid-row">
          <span class="coid-label">Service Date and Time:</span>
          <span class="coid-value coid-value--service-datetime coid-value--body-text">{{
            serviceDateTime
          }}</span>
        </div>
        <div class="coid-row">
          <span class="coid-label">Inspector:</span>
          <span class="coid-value coid-value--inspector coid-value--body-text">{{
            inspectorName
          }}</span>
        </div>
        <div class="coid-row">
          <span class="coid-label">Status:</span>
          <span class="coid-value coid-value--status">
            <img :src="imgErrorRed" class="coid-status-ic" alt="" aria-hidden="true" />
            <span class="coid-status-txt coid-status-txt--major">Major Issue</span>
          </span>
        </div>
        <div class="coid-row">
          <span class="coid-label">Condition Rate:</span>
          <span class="coid-value coid-value--condition-rate coid-value--body-text">{{
            conditionRateDisplay
          }}</span>
        </div>
        <div class="coid-row coid-row--top">
          <span class="coid-label">Condition:</span>
          <div class="coid-value coid-value--stack">
            <span class="coid-text-plain coid-value--body-text">{{ conditionSummary }}</span>
            <p class="coid-para coid-value--body-text">{{ conditionNote }}</p>
          </div>
        </div>
        <div class="coid-row coid-row--top">
          <span class="coid-label">Location:</span>
          <div class="coid-value coid-value--stack">
            <span class="coid-text-plain coid-value--body-text">{{ locationDetail }}</span>
            <p class="coid-para coid-value--body-text">{{ locationNote }}</p>
          </div>
        </div>
        <div class="coid-row coid-row--top">
          <span class="coid-label">Task:</span>
          <div class="coid-value coid-value--stack">
            <span class="coid-text-plain coid-value--body-text">{{ taskPlainLabel }}</span>
            <p class="coid-para coid-value--body-text">{{ taskNote }}</p>
          </div>
        </div>
        <div class="coid-row coid-row--top">
          <span class="coid-label">Time:</span>
          <div class="coid-value coid-value--stack">
            <span class="coid-text-plain coid-value--body-text">{{ timePlainLabel }}</span>
            <p class="coid-para coid-value--body-text">{{ timeNote }}</p>
          </div>
        </div>
        <div v-if="previewPhotoUrls.length" class="coid-row coid-row--top">
          <span class="coid-label">Photos:</span>
          <div class="coid-value coid-value--stack">
            <div class="coid-photos">
              <button
                v-for="(item, i) in previewPhotoUrls"
                :key="`major-preview-${i}`"
                type="button"
                class="coid-photo-btn"
                @click="openPhoto(i)"
              >
                <video
                  v-if="item.isVideo"
                  :src="item.url"
                  class="coid-photo"
                  muted
                  playsinline
                  preload="metadata"
                />
                <img v-else :src="item.url" class="coid-photo" alt="" />
              </button>
            </div>
            <button
              v-if="hasMorePhotos"
              type="button"
              class="coid-all-link"
              @click="openAllPhotos"
            >
              All
              <img :src="imgRightNarrow" class="coid-all-chev" alt="" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div class="coid-row coid-row--top coid-row--general-desc">
          <span class="coid-label coid-label--general">General Description:</span>
          <div class="coid-value coid-value--stack">
            <p
              v-for="(p, i) in generalDescriptionParagraphs"
              :key="`major-g-${i}`"
              class="coid-para coid-value--body-text"
            >
              {{ p }}
            </p>
          </div>
        </div>
      </div>

      <template v-else>
        <div class="coid-block">
          <div class="coid-row">
            <span class="coid-label">Report ID:</span>
            <div class="coid-value coid-value--report">
              <span class="coid-report-id">{{ reportId }}</span>
              <div class="coid-date-anchor">
                <button
                  ref="dateBtnRef"
                  type="button"
                  class="coid-date-btn"
                  :aria-expanded="reportDateDropOpen"
                  aria-haspopup="listbox"
                  aria-label="View past inspection records"
                  @click.stop="toggleReportDateDrop"
                >
                  <span class="coid-date-txt">{{ reportDateDisplay }}</span>
                  <img :src="imgChevron" class="coid-date-chev" alt="" aria-hidden="true" />
                </button>
                <div
                  v-if="reportDateDropOpen && inspectionHistoryRecords.length > 1"
                  ref="dateDropRef"
                  class="coid-date-drop"
                  role="listbox"
                  aria-label="Past inspection records"
                  @mousedown.stop
                  @click.stop
                >
                  <button
                    v-for="rec in inspectionHistoryRecords"
                    :key="rec.key"
                    type="button"
                    class="coid-date-drop-item"
                    :class="{ 'coid-date-drop-item--active': rec.key === activeInspectionRecord?.key }"
                    role="option"
                    :aria-selected="rec.key === activeInspectionRecord?.key"
                    @click="onSelectInspectionRecord(rec)"
                  >
                    {{ rec.dateLabel }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="coid-row">
            <span class="coid-label">Service Date and Time:</span>
            <span class="coid-value coid-value--service-datetime">{{ serviceDateTime }}</span>
          </div>
          <div class="coid-row">
            <span class="coid-label">Inspector:</span>
            <span class="coid-value coid-value--inspector">{{ inspectorName }}</span>
          </div>
          <div
            class="coid-row"
            :class="{ 'coid-row--top': showMajorStatusTimeline && showMajorCoveredFollowUpStep }"
          >
            <span class="coid-label">Status:</span>
            <div v-if="showMajorStatusTimeline" class="coid-value coid-status-timeline">
              <div class="coid-status-step">
                <img :src="imgErrorRed" class="coid-status-ic" alt="" aria-hidden="true" />
                <span class="coid-status-txt coid-status-txt--major">Major Issue</span>
              </div>
              <template v-if="showMajorCoveredFollowUpStep">
                <div class="coid-status-timeline-connector" aria-hidden="true" />
                <div class="coid-status-step">
                  <img
                    :src="isMajorCoveredResolvedNormal ? imgNormal : imgUnable"
                    class="coid-status-ic"
                    alt=""
                    aria-hidden="true"
                  />
                  <span
                    :class="
                      isMajorCoveredResolvedNormal
                        ? 'coid-status-txt coid-status-txt--normal'
                        : 'coid-status-txt coid-status-txt--unable'
                    "
                    >{{ isMajorCoveredResolvedNormal ? 'Normal' : 'Unable to Repair' }}</span
                  >
                </div>
              </template>
            </div>
            <span v-else class="coid-value coid-value--status">
              <img :src="statusIconSrc" class="coid-status-ic" alt="" aria-hidden="true" />
              <span :class="statusTextClass">{{ statusDisplayLabel }}</span>
            </span>
          </div>
          <div v-if="!isCompactDetail" class="coid-row">
            <span class="coid-label">Condition Rate:</span>
            <span class="coid-value coid-value--condition-rate">{{ conditionRateDisplay }}</span>
          </div>

          <template v-if="isCompactDetail">
            <div v-if="previewPhotoUrls.length" class="coid-row coid-row--top">
              <span class="coid-label">Photos:</span>
              <div class="coid-value coid-value--stack">
                <div class="coid-photos">
                  <button
                    v-for="(item, i) in previewPhotoUrls"
                    :key="`preview-${i}`"
                    type="button"
                    class="coid-photo-btn"
                    @click="openPhoto(i)"
                  >
                    <video
                      v-if="item.isVideo"
                      :src="item.url"
                      class="coid-photo"
                      muted
                      playsinline
                      preload="metadata"
                    />
                    <img v-else :src="item.url" class="coid-photo" alt="" />
                  </button>
                </div>
                <button
                  v-if="hasMorePhotos"
                  type="button"
                  class="coid-all-link"
                  @click="openAllPhotos"
                >
                  All
                  <img :src="imgRightNarrow" class="coid-all-chev" alt="" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div class="coid-row coid-row--top coid-row--general-desc">
              <span class="coid-label coid-label--general">General Description:</span>
              <div class="coid-value coid-value--stack">
                <p v-for="(p, i) in generalDescriptionParagraphs" :key="`g-${i}`" class="coid-para">
                  {{ p }}
                </p>
              </div>
            </div>
          </template>
        </div>

        <template v-if="showInspectionDetailBlock && !viewingFollowUpInspectionRecord">
          <div class="coid-divider" />

          <div class="coid-block coid-block--inspection-body">
            <div class="coid-row coid-row--top">
              <span class="coid-label">Condition:</span>
              <div class="coid-value coid-value--stack">
                <span class="coid-text-plain coid-value--body-text">{{ conditionSummary }}</span>
                <p v-if="conditionNote" class="coid-para coid-value--emph">{{ conditionNote }}</p>
              </div>
            </div>
            <div class="coid-row coid-row--top">
              <span class="coid-label">Location:</span>
              <div class="coid-value coid-value--stack">
                <span class="coid-text-plain coid-value--body-text">{{ locationDetail }}</span>
                <p v-if="locationNote" class="coid-para coid-value--emph">{{ locationNote }}</p>
              </div>
            </div>
            <div class="coid-row coid-row--top">
              <span class="coid-label">Task:</span>
              <div class="coid-value coid-value--stack">
                <span class="coid-text-plain coid-value--body-text">{{ taskPlainLabel }}</span>
                <p v-if="taskNote" class="coid-para coid-value--emph">{{ taskNote }}</p>
              </div>
            </div>
            <div class="coid-row coid-row--top">
              <span class="coid-label">Time:</span>
              <div class="coid-value coid-value--stack">
                <span class="coid-text-plain coid-value--body-text">{{ timePlainLabel }}</span>
                <p v-if="timeNote" class="coid-para coid-value--emph">{{ timeNote }}</p>
              </div>
            </div>
            <div v-if="previewPhotoUrls.length" class="coid-row coid-row--top">
              <span class="coid-label">Photos:</span>
              <div class="coid-value coid-value--stack">
                <div class="coid-photos">
                  <button
                    v-for="(item, i) in previewPhotoUrls"
                    :key="`preview-${i}`"
                    type="button"
                    class="coid-photo-btn"
                    @click="openPhoto(i)"
                  >
                    <video
                      v-if="item.isVideo"
                      :src="item.url"
                      class="coid-photo"
                      muted
                      playsinline
                      preload="metadata"
                    />
                    <img v-else :src="item.url" class="coid-photo" alt="" />
                  </button>
                </div>
                <button
                  v-if="hasMorePhotos"
                  type="button"
                  class="coid-all-link"
                  @click="openAllPhotos"
                >
                  All
                  <img :src="imgRightNarrow" class="coid-all-chev" alt="" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div v-if="generalDescriptionParagraphs.length" class="coid-row coid-row--top coid-row--general-desc">
              <span class="coid-label coid-label--general">General Description:</span>
              <div class="coid-value coid-value--stack">
                <p
                  v-for="(p, i) in generalDescriptionParagraphs"
                  :key="`g-${i}`"
                  class="coid-para coid-value--body-text"
                >
                  {{ p }}
                </p>
              </div>
            </div>
          </div>
        </template>

        <template v-if="isMajorCoveredDetail">
          <div class="coid-divider" />
          <div class="coid-block">
            <div class="coid-row">
              <span class="coid-label">Report ID:</span>
              <span class="coid-value">{{ followUpReportId }}</span>
            </div>
            <div class="coid-row coid-row--top">
              <span class="coid-label coid-label--follow-up-dt">Follow-up Date and Time:</span>
              <span class="coid-value coid-value--service-datetime">{{
                followUpDateTimeDisplay
              }}</span>
            </div>
            <div class="coid-row">
              <span class="coid-label">Technician:</span>
              <span class="coid-value coid-value--inspector">{{ followUpTechnicianName }}</span>
            </div>
            <div v-if="followUpPhotoUrls.length" class="coid-row coid-row--top">
              <span class="coid-label">Photos:</span>
              <div class="coid-value coid-value--stack">
                <div class="coid-photos">
                  <button
                    v-for="(item, i) in followUpPhotoUrls.slice(0, 1)"
                    :key="`fu-preview-${i}`"
                    type="button"
                    class="coid-photo-btn"
                    @click="openFollowUpPhoto(i)"
                  >
                    <video
                      v-if="item.isVideo"
                      :src="item.url"
                      class="coid-photo"
                      muted
                      playsinline
                      preload="metadata"
                    />
                    <img v-else :src="item.url" class="coid-photo" alt="" />
                  </button>
                </div>
              </div>
            </div>
            <div class="coid-row coid-row--top">
              <span class="coid-label coid-label--general">Follow-up Notes:</span>
              <div class="coid-value coid-value--stack">
                <p v-for="(p, i) in followUpNotesParagraphs" :key="`fu-${i}`" class="coid-para">
                  {{ p }}
                </p>
              </div>
            </div>
          </div>
        </template>
      </template>
    </template>
  </div>
</template>

<style scoped>
.coid {
  --coid-v-gap: 24px;
  /*
   * Check: 相对白底弹窗 left 108px、宽 45px；父级 .cod-inline-detail 已有 24px 左右 pad，
   * 故内容区内 Check 左缘 = 108 - 24 = 84px，冒号轴 = 84 + 45 = 129px。
   */
  --coid-sheet-pad: 24px;
  --coid-check-left-from-sheet: 108px;
  --coid-check-width: 45px;
  --coid-label-col: calc(
    var(--coid-check-left-from-sheet) - var(--coid-sheet-pad) + var(--coid-check-width)
  );
  --coid-label-value-gap: 24px;
  /* 小标题：SF Pro Regular 14/20 #000 */
  --coid-label-font:
    'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  /* 正文：SF Pro Regular 14/20 #000，左对齐 */
  --coid-value-font:
    'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  padding: 0;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.coid-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0 0 var(--coid-v-gap);
  cursor: pointer;
}

/* Back：SF Pro Regular 14/20 #000，设计稿右对齐 */
.coid-back-txt {
  margin: 0;
  font-family: var(--coid-value-font);
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  text-align: right;
}

.coid-back-ic {
  width: 16px;
  height: 16px;
  display: block;
  flex-shrink: 0;
  object-fit: contain;
}

.coid-block {
  margin-bottom: 0;
}

.coid-row {
  display: grid;
  grid-template-columns: var(--coid-label-col) minmax(0, 1fr);
  column-gap: var(--coid-label-value-gap);
  row-gap: 12px;
  align-items: center;
  padding: calc(var(--coid-v-gap) / 2) 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.coid-row--top {
  align-items: start;
}

.coid-row--general-desc {
  grid-template-columns: var(--coid-label-col) minmax(0, 1fr);
}

.coid-label,
.coid-label--general,
.coid-label--follow-up-dt {
  margin: 0;
  box-sizing: border-box;
  width: 100%;
  font-family: var(--coid-label-font);
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  /* 列右缘 = Check 冒号轴，右对齐保证各行冒号共线 */
  text-align: right;
}

/* Check: 设计稿 45×20、居中；靠右贴冒号轴 */
.coid-label--check {
  display: block;
  width: var(--coid-check-width);
  height: 20px;
  margin-left: auto;
  margin-right: 0;
  text-align: center;
  white-space: nowrap;
}

.coid-label--follow-up-dt {
  max-width: 100%;
}

/* 正文（小标题后的内容、Description、Report ID、Follow-up Notes 等） */
.coid-value,
.coid-para,
.coid-para--description,
.coid-text-plain,
.coid-report-id,
.coid-all-link {
  margin: 0;
  font-family: var(--coid-value-font);
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  text-align: left;
}

/* Condition Rate（如 50 → 100）：正文 SF Pro Regular 14/20 #000 左对齐 */
.coid-value--condition-rate {
  margin: 0;
}

/* 报告正文（Report ID、日期、Inspector 等）：#333，非深黑 */
.coid-value--body-text {
  color: #333333;
  -webkit-text-fill-color: #333333;
}

.coid-row:has(.coid-value--inspector) {
  align-items: start;
}

.coid-value--inspector {
  display: block;
  min-width: 0;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.coid-block--major-report .coid-all-link {
  color: #333333;
}

.coid-value--stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  max-width: 100%;
}

.coid-text-plain {
  text-decoration: none;
}

.coid-value--description {
  gap: 0;
}

.coid-seasons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.coid-season-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 54px;
  height: 20px;
  padding: 2px 6px;
  border-radius: 8px;
  background: #f7f7f7;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0;
  color: #999999;
  text-align: center;
  white-space: nowrap;
}

.coid-season-pill--summer,
.coid-season-pill--winter {
  min-width: 58px;
}

.coid-divider {
  height: 1px;
  background: #e8e8e8;
  margin: var(--coid-v-gap) 0;
}

.coid-value--report {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 12px;
}

.coid-report-id {
  flex-shrink: 0;
}

.coid-date-anchor {
  position: relative;
  flex-shrink: 0;
  margin-left: auto;
}

.coid-date-drop {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 12700;
  box-sizing: border-box;
  width: 122px;
  background: #fff;
  border: 1px solid #0c3637;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.coid-date-drop-item {
  display: block;
  width: 100%;
  border: none;
  background: #fff;
  padding: 8px 12px;
  font-family: var(--coid-value-font);
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: #000;
  text-align: left;
  cursor: pointer;
}

.coid-date-drop-item:hover,
.coid-date-drop-item--active {
  background: #f7f7f7;
}

/* Report ID 右侧日期胶囊外框：122×28，圆角 16px，描边 #0C3637 */
.coid-date-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  flex-shrink: 0;
  box-sizing: border-box;
  width: 122px;
  height: 28px;
  padding: 0 8px;
  border-radius: 16px;
  border: 1px solid #0c3637;
  background: #fff;
  cursor: pointer;
  white-space: nowrap;
}

/* Report ID 右侧日期胶囊内文字：SF Pro Regular 14/20 #000 居中 */
.coid-date-txt {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-family: var(--coid-value-font);
  font-size: 13px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  white-space: nowrap;
  text-align: center;
}

.coid-date-chev {
  width: 16px;
  height: 16px;
  display: block;
  flex-shrink: 0;
  object-fit: contain;
}

.coid-value--status {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  text-align: left;
}

/* Status 前图标：16×16 */
.coid-status-ic {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  max-width: 16px;
  max-height: 16px;
  display: block;
  object-fit: contain;
  flex-shrink: 0;
}

/* Status 文案：SF Pro Regular 14/20；Pending Care 为 #ccc */
.coid-status-txt {
  margin: 0;
  font-family: var(--coid-value-font);
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  text-align: left;
}

.coid-status-txt--attention {
  color: #ffb407;
}

/* Normal：SF Pro Regular 14/20 #4A7A2B 左对齐；前图标 16×16（normal@2x） */
.coid-status-txt--normal {
  color: #4a7a2b;
}

.coid-status-txt--unable {
  color: #000000;
}

.coid-status-txt--pending {
  color: #cccccc;
}

/* Major Issue：SF Pro Regular 14/20 #E54848 左对齐；前图标 16×16（error.red@2x） */
.coid-status-txt--major {
  color: #e54848;
}

.coid-status-timeline {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
}

.coid-status-step {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* Major Issue 与 Normal 之间连接：16×28 */
.coid-status-timeline-connector {
  display: block;
  width: 16px;
  height: 28px;
  min-width: 16px;
  min-height: 28px;
  margin: 0;
  flex-shrink: 0;
  background: center / 16px 28px no-repeat
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='28' viewBox='0 0 16 28'%3E%3Ccircle cx='8' cy='4' r='2' fill='%23CCCCCC'/%3E%3Ccircle cx='8' cy='10' r='2' fill='%23CCCCCC'/%3E%3Ccircle cx='8' cy='16' r='2' fill='%23CCCCCC'/%3E%3Ccircle cx='8' cy='22' r='2' fill='%23CCCCCC'/%3E%3C/svg%3E");
}

.coid-value--emph {
  margin: 0;
  font-style: italic;
  text-decoration: underline;
  color: #333333;
}

.coid-photos {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  max-width: 100%;
}

.coid-photo-btn {
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
  line-height: 0;
  width: 102px;
  height: 102px;
  flex-shrink: 0;
}

.coid-photo {
  width: 102px;
  height: 102px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
  background: #f0f1f3;
}

.coid-photo-btn video,
.coid-photo-grid-btn video {
  pointer-events: none;
}

.coid-photo-preview {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 16px;
  object-fit: cover;
  background: #f0f1f3;
}

.coid-all-link {
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 0;
  margin-top: 4px;
  text-decoration: none;
  cursor: pointer;
}

.coid-all-chev {
  width: 16px;
  height: 16px;
  display: block;
  flex-shrink: 0;
  object-fit: contain;
}

.coid-photo-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.coid-photo-grid-btn {
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
  line-height: 0;
  width: 100%;
}

.coid-photo-grid-item {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  object-fit: cover;
  display: block;
  background: #f0f1f3;
}
</style>
